import { Store } from '@ngrx/store';
import { Component, input, signal, Input, Signal, computed, effect, model, untracked, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { KENDO_COMBOBOX } from "@progress/kendo-angular-dropdowns";
import { IVessel, selectLocationsItems, ILocation, IVesselVisitOrganization, IOrganizationType, IOrganizationEdit } from '@pmis3/master-data-core';
import { VesselVisitDetailLabelledInformationComponent } from "../vessel-visit-detail-labelled-information/vessel-visit-detail-labelled-information.component"
import { KENDO_LABEL } from "@progress/kendo-angular-label";
import { VesselVisitDetailOrganizationComponent } from "../vessel-visit-detail-organization/vessel-visit-detail-organization.component"
import { KENDO_TEXTBOX, KENDO_INPUTS } from "@progress/kendo-angular-inputs";
import { FormsModule } from "@angular/forms";
import { IconsModule } from '@progress/kendo-angular-icons';
import { KENDO_DATEINPUTS } from '@progress/kendo-angular-dateinputs';
import { IVesselVisit, selectSelectedVesselVisit, UpdateSelectedVesselVisit, VesselVisitHelper } from '@pmis3/vessel-activity-core';
import { SeaportPickerComponent, ISeaport} from '@pmis3/ui-features';
import { signalState, patchState } from '@ngrx/signals';
import { DateTimeFormat } from '@pmis3/core';
@Component({
    selector: 'pmis3-vessel-visit-detail-noa',
    standalone: true,
    imports: [
        CommonModule,
        KENDO_COMBOBOX,
        KENDO_LABEL,
        VesselVisitDetailOrganizationComponent,
        KENDO_TEXTBOX,
        FormsModule,
        IconsModule,
        KENDO_DATEINPUTS,
        KENDO_INPUTS,
        SeaportPickerComponent
    ],
    templateUrl:    'vessel-visit-detail-noa.component.html',
    styleUrl:       './vessel-visit-detail-noa.component.css',
})
export class VesselVisitDetailNOAComponent {
    collapsed = input(false);
    dateFormat = DateTimeFormat.kendoDateTimeFormat;
    @Output() allFieldsValidChange = new EventEmitter<boolean>();

    // seaports        = this.store.selectSignal(selectAllSeaports);
    seaports = input<ISeaport[]>([])
    vesselVisit     = this.store.selectSignal(selectSelectedVesselVisit);
    VesselVisitFieldKey  = VesselVisitFieldKey;

    state = signalState({
        lastPortOfCallId:               undefined as number | undefined,
        nextPortOfCallId:               undefined as number | undefined,
        ntaParticipationDeclaration:    undefined as boolean | undefined,
        vesselMasterName:               '',
        vesselPhone:                    '',
        vesselEmail:                    '',
        agentName:                      '',
        agentPhone:                     '',
        agentEmail:                     '',
        remarks:                        '',
        organizations:                  [] as IVesselVisitOrganization[],

        // These are duplicates and may be removed in the future
        pilotBoardingETA:               null as Date | null,
        pilotBoardingATA:               null as Date | null,
        berthETA:                       null as Date | null
    });

    vessel      = computed(() => this.vesselVisit()?.vessel);
    disabled    = computed(() => !this.vessel() || this.collapsed());

    updatedVesselVisit = computed(() => {
        const vv        = this.vesselVisit();
        const seaports  = this.seaports();
        const state     = this.state();

        if (!vv) return null;

        return {
            ...vv,

            lastPortOfCallId:               state.lastPortOfCallId,
            lastPortOfCall:                 seaports.find(p => p.id === state.lastPortOfCallId) ?? null,
            nextPortOfCallId:               state.nextPortOfCallId,
            nextPortOfCall:                 seaports.find(p => p.id === state.nextPortOfCallId) ?? null,
            remarks:                        state.remarks,
            hasNTAParticipationDeclaration: state.ntaParticipationDeclaration,
            vesselMasterName:               state.vesselMasterName,
            vesselContactPhone:             state.vesselPhone,
            vesselContactEmail:             state.vesselEmail,
            agentName:                      state.agentName,
            agent247ContactPhone:           state.agentPhone,
            agent247ContactEmail:           state.agentEmail,
            organizations:                  state.organizations,

            // These are duplicates and may be removed in the future
            pilotBoardingETA:               state.pilotBoardingETA,
            pilotBoardingATA:               state.pilotBoardingATA,
            berthETA:                       state.berthETA
        } as IVesselVisit;
    });

    vesselVisitIsValid = computed(() => {
        const vv        = this.updatedVesselVisit();
        const isValid   = vv ? VesselVisitHelper.vesselVisitIsValid(vv) : false;
        this.allFieldsValidChange.emit(isValid);
        return isValid;
    });

    constructor(private store: Store<any>) {
        effect(() => {
            const vv = this.vesselVisit();
            if (vv) {
                untracked(() => {
                    patchState(this.state, {
                        lastPortOfCallId:               vv.lastPortOfCallId,
                        nextPortOfCallId:               vv.nextPortOfCallId,
                        ntaParticipationDeclaration:    vv.hasNTAParticipationDeclaration,
                        vesselMasterName:               vv.vesselMasterName     ?? '',
                        vesselPhone:                    vv.vesselContactPhone   ?? '',
                        vesselEmail:                    vv.vesselContactEmail   ?? '',
                        agentName:                      vv.agentName            ?? '',
                        agentPhone:                     vv.agent247ContactPhone ?? '',
                        agentEmail:                     vv.agent247ContactEmail ?? '',
                        remarks:                        vv.remarks              ?? '',
                        organizations:                  vv.organizations        ?? [],
                        
                        // These are duplicates and may be removed in the future
                        pilotBoardingETA:               vv.pilotBoardingETA     ?? null,
                        pilotBoardingATA:               vv.pilotBoardingATA     ?? null,
                        berthETA:                       vv.berthETA             ?? null
                    })
                })
            }
        })

        effect(() => {
            const allFieldsValid = this.vesselVisitIsValid();
            this.allFieldsValidChange.emit(allFieldsValid);
        })
    }

    updateVisit() {
        const vv = this.updatedVesselVisit();
        if (vv) {
            this.store.dispatch(UpdateSelectedVesselVisit({ selectedVesselVisit: vv }));
        }
    }

    onValueChange(key: VesselVisitFieldKey, value: any) {
        patchState(this.state, { [key]: value });
        this.updateVisit();
    }

    visitFieldIsValid(key: keyof IVesselVisit) {
        const vv = this.updatedVesselVisit();
        return vv ? VesselVisitHelper.visitFieldIsValid(key, vv) || this.disabled() : false;
    }

    onAssignedOrganizationFromChild(organization: IOrganizationEdit) {
        const vesselVisitId = this.vesselVisit()?.id;
        if (vesselVisitId == undefined) return;

        const originalOrganizations = this.state()?.organizations ?? [];
        const matchedOrganization   = originalOrganizations.find(org =>
            org.organizationId      == organization.organizationId && 
            org.organizationTypeId  == organization.organizationTypeId
        )

        let vesselVisitOrganization: IVesselVisitOrganization;
        if (!matchedOrganization) {
            vesselVisitOrganization = {
                ...organization,
                vesselVisitId: vesselVisitId,
                isAdded: true
            }
            patchState(this.state, (s) => ({
            ...s,
            organizations: [...s.organizations, vesselVisitOrganization]
        }));
        } else {
            vesselVisitOrganization = {
                ...matchedOrganization,
                isAdded: false,
                isDeleted: false
            }
            patchState(this.state, (s) => ({
                ...s,
                organizations: s.organizations.map(org =>
                    org.organizationId      == matchedOrganization.organizationId &&
                    org.organizationTypeId  == matchedOrganization.organizationTypeId
                        ? vesselVisitOrganization
                        : org
                )
            }));
        }

        this.updateVisit();
    }

    onDeletedOrganizationFromChild(deletedOrganization: IOrganizationEdit) {
        const vesselVisitId = this.vesselVisit()?.id
        if (vesselVisitId == undefined) return;

        const originalOrganizations = this.state()?.organizations ?? [];
        const matchedOrganization   = originalOrganizations.find(
            org => org.organizationId == deletedOrganization.organizationId && 
            org.organizationTypeId    == deletedOrganization.organizationTypeId
        );

        if (matchedOrganization) {
            patchState(this.state, (s) => ({
                ...s,
                organizations: s.organizations.map(org =>
                    org.organizationId      == matchedOrganization.organizationId &&
                    org.organizationTypeId  == matchedOrganization.organizationTypeId
                        ? { ...deletedOrganization, vesselVisitId: vesselVisitId }
                        : org
                )
            }));
        } else {
            patchState(this.state, (s) => ({
                ...s,
                organizations: s.organizations.filter(
                    org =>
                        org.organizationId      !== deletedOrganization.organizationId ||
                        org.organizationTypeId  !== deletedOrganization.organizationTypeId
                )
            }));
        }
        this.updateVisit();
    }

    arrowUrl() {
        return 'assets/icons/diraction.svg';
    }
}

enum VesselVisitFieldKey {
  LastPortOfCallId              = 'lastPortOfCallId',
  NextPortOfCallId              = 'nextPortOfCallId',
  NtaParticipationDeclaration   = 'ntaParticipationDeclaration',
  VesselMasterName              = 'vesselMasterName',
  VesselPhone                   = 'vesselPhone',
  VesselEmail                   = 'vesselEmail',
  AgentName                     = 'agentName',
  AgentPhone                    = 'agentPhone',
  AgentEmail                    = 'agentEmail',
  Remarks                       = 'remarks',
  Organizations                 = 'organizations',
  PilotBoardingETA              = 'pilotBoardingETA',
  PilotBoardingATA              = 'pilotBoardingATA',
  BerthETA                      = 'berthETA'
}